// The phone's location.
//
// The first answer a phone gives is often a quick guess from Wi-Fi or cell towers, kilometres off; GPS sharpens it
// within a few seconds. So this listens for a while (watchPosition, no cached fixes) and keeps the most accurate
// answer: it finishes as soon as one is accurate to `goodM`, or after `maxWaitMs` with the best so far.
// `onFix` sees every improvement, so a map can show the dot at once and tighten it as GPS settles.

export interface Position { lat: number; lon: number; acc: number }
export interface PositionOptions {
  /** Good enough: finish as soon as a fix is this accurate (m). */
  goodM?: number;
  /** Give up waiting for better and use the best fix after this long (ms). */
  maxWaitMs?: number;
  /** Called with each fix that is more accurate than the ones before. */
  onFix?: (p: Position) => void;
}

export function getPosition(opts: PositionOptions = {}): Promise<Position> {
  const goodM = opts.goodM ?? 30, maxWaitMs = opts.maxWaitMs ?? 10000;
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) { reject(new Error("unsupported")); return; }
    let best: Position | null = null, done = false, watch = 0;
    const finish = (err?: unknown) => {
      if (done) return;
      done = true;
      navigator.geolocation.clearWatch(watch);
      clearTimeout(timer); clearTimeout(hardStop);
      if (best) resolve(best); else reject(err ?? new Error("no position"));
    };
    // after maxWaitMs use the best fix so far; with none yet, keep waiting up to 25 s for the first one
    const timer = setTimeout(() => { if (best) finish(); }, maxWaitMs);
    const hardStop = setTimeout(() => finish(new Error("timeout")), Math.max(25000, maxWaitMs));
    watch = navigator.geolocation.watchPosition((p) => {
      const fix = { lat: p.coords.latitude, lon: p.coords.longitude, acc: Math.round(p.coords.accuracy) };
      if (best && fix.acc >= best.acc) return;
      best = fix;
      opts.onFix?.(fix);
      if (fix.acc <= goodM) finish();
    }, (err) => { if (err.code === err.PERMISSION_DENIED || !best) finish(err); },
    { enableHighAccuracy: true, timeout: 25000, maximumAge: 0 });
  });
}

/** Rough even after waiting: usually Android's "approximate location" for this app. */
export const ROUGH_M = 500;
